/**
 * Timezone-aware calendar helpers. All "keys" are computed in the family's timezone so a
 * daily reset happens at local midnight no matter where the server runs.
 */

export interface LocalParts {
  /** YYYY-MM-DD */
  date: string;
  /** 0 = Sunday ... 6 = Saturday */
  dow: number;
  /** minutes since local midnight */
  minutes: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

export function isValidTimezone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return true;
  } catch {
    return false;
  }
}

export function localParts(now: number, timeZone: string): LocalParts {
  const parts = formatter(isValidTimezone(timeZone) ? timeZone : "UTC").formatToParts(new Date(now));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "0";
  const date = `${get("year")}-${get("month")}-${get("day")}`;
  const hour = Number(get("hour")) % 24;
  return { date, dow: dowOfDate(date), minutes: hour * 60 + Number(get("minute")) };
}

/** Day of week for a YYYY-MM-DD calendar date. */
export function dowOfDate(date: string): number {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

export function addDays(date: string, days: number): string {
  const [y, m, d] = date.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

export function dayKey(now: number, timeZone: string): string {
  return localParts(now, timeZone).date;
}

/** The local date the current week started on, prefixed with "W". */
export function weekKey(now: number, timeZone: string, weekStartsOn: number): string {
  const { date, dow } = localParts(now, timeZone);
  const offset = (dow - weekStartsOn + 7) % 7;
  return `W${addDays(date, -offset)}`;
}

export function periodKey(frequency: "daily" | "weekly" | "once", now: number, timeZone: string, weekStartsOn: number) {
  if (frequency === "once") return "once";
  if (frequency === "weekly") return weekKey(now, timeZone, weekStartsOn);
  return dayKey(now, timeZone);
}

export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
export const ALL_DAYS = 127;
export const WEEKDAYS = 0b0111110; // Mon-Fri
export const WEEKENDS = 0b1000001; // Sat, Sun
/** "School nights" are Sunday-Thursday evenings. */
export const NOT_SCHOOL_NIGHTS = 0b1100000; // Fri, Sat

export function dayInMask(mask: number, dow: number): boolean {
  return (mask & (1 << dow)) !== 0;
}

export function maskToDays(mask: number): number[] {
  return [0, 1, 2, 3, 4, 5, 6].filter((d) => dayInMask(mask, d));
}

export function daysToMask(days: number[]): number {
  return days.reduce((m, d) => m | (1 << d), 0);
}

export function describeDays(mask: number): string {
  if (mask === ALL_DAYS) return "Every day";
  if (mask === WEEKDAYS) return "Weekdays";
  if (mask === WEEKENDS) return "Weekends";
  if (mask === 0) return "Never";
  return maskToDays(mask)
    .map((d) => DAY_NAMES[d])
    .join(", ");
}

export function parseHHMM(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function formatHHMM(value: string | null | undefined): string {
  const minutes = parseHHMM(value);
  if (minutes === null) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}${suffix}` : `${h12}:${String(m).padStart(2, "0")}${suffix}`;
}
